import { resolveEffectiveSkills } from "@common/helpers/agentSkillBundle";
import type { AgentSkillSummaryConsumer } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import {
    AgentSkillLoadMode,
    type AgentSkillCategory,
    type AgentSkillKind,
} from "@common/types/agentSkill";

interface BundleAgentSkill {
    kind: AgentSkillKind;
    slug: string;
    organizationId: string | null;
    workspaceId: string | null;
    loadMode: AgentSkillLoadMode;
    isEnabled: boolean;
}

export const countEffectiveAlwaysSkills = ({
    skills,
    clientWorkspaceId,
}: {
    skills: BundleAgentSkill[];
    clientWorkspaceId: string | null;
}): number =>
    resolveEffectiveSkills({
        skills: skills.filter((skill) => skill.isEnabled),
        workspaceId: clientWorkspaceId,
    }).filter((skill) => skill.loadMode === AgentSkillLoadMode.ALWAYS).length;

interface FilterableAgentSkill {
    title: string;
    description: string;
    category: AgentSkillCategory;
    summary: { consumers: AgentSkillSummaryConsumer[] } | null;
}

export interface AgentSkillsFilters {
    search: string;
    category: AgentSkillCategory | null;
    consumer: AgentSkillSummaryConsumer | null;
}

export const DEFAULT_AGENT_SKILLS_FILTERS: AgentSkillsFilters = {
    search: "",
    category: null,
    consumer: null,
};

export const filterAgentSkills = <T extends FilterableAgentSkill>({
    skills,
    filters,
}: {
    skills: T[];
    filters: AgentSkillsFilters;
}): T[] => {
    const search = filters.search.trim().toLowerCase();
    return skills.filter((skill) => {
        const matchesSearch =
            search === "" ||
            skill.title.toLowerCase().includes(search) ||
            skill.description.toLowerCase().includes(search);
        const matchesCategory =
            filters.category === null || skill.category === filters.category;
        const matchesConsumer =
            filters.consumer === null ||
            (skill.summary?.consumers ?? []).includes(filters.consumer);
        return matchesSearch && matchesCategory && matchesConsumer;
    });
};
